import { ApolloClient } from '@apollo/client'
import chunk from 'lodash/chunk'
import { advancedGet } from '../api/client'
import { AdvancedCareTeamMember } from '../api/types'
import { GetAdvancedAllTagsDocument, GetAdvancedAllTagsQuery } from '../api/advancedPatientTags.generated'
import { mapWithConcurrency } from './adminCareTeamBatch'
import {
  AdvancedMembershipReviewPatientFragment,
  GetAdvancedMembershipReviewPatientsDocument,
  GetAdvancedMembershipReviewPatientsQuery,
  GetAdvancedMembershipReviewPatientsQueryVariables,
  SearchAdvancedMembershipReviewPatientsDocument,
  SearchAdvancedMembershipReviewPatientsQuery,
  SearchAdvancedMembershipReviewPatientsQueryVariables,
} from './adminMembershipReview.generated'
import { ADVANCED_PROGRAM_TAG_NAME } from './advancedProgramTag'

type ReviewClient = Pick<ApolloClient<object>, 'query'>
export type ReviewPatient = AdvancedMembershipReviewPatientFragment
type MembershipPage = { memberships: AdvancedCareTeamMember[]; nextCursor: string | null }
export type MembershipReview = {
  memberships: AdvancedCareTeamMember[]
  patients: ReviewPatient[]
  hasAdvancedTag: boolean
  unavailablePatientUids: string[]
}
export type ProviderMembershipReview = {
  providerUid: string
  advancedPatients: { uid: string; roles: string[] }[]
  missingMembershipPatients: string[]
  missingStandardMembershipPatients: string[]
}

const PATIENT_PAGE_SIZE = 499
const abortIfNeeded = (signal: AbortSignal) => {
  if (signal.aborted) throw new Error('Membership review cancelled')
}

const loadMemberships = async (signal: AbortSignal): Promise<AdvancedCareTeamMember[]> => {
  const memberships: AdvancedCareTeamMember[] = []
  let cursor: string | null = null
  const seen = new Set<string>()
  do {
    abortIfNeeded(signal)
    const path = `/admin/care-teams/memberships${cursor ? `?after=${encodeURIComponent(cursor)}` : ''}`
    // eslint-disable-next-line no-await-in-loop -- Each page supplies the next cursor.
    const page: MembershipPage = await advancedGet<MembershipPage>(path, signal)
    memberships.push(...page.memberships)
    cursor = page.nextCursor
    if (cursor && seen.has(cursor)) throw new Error('Incomplete membership review')
    if (cursor) seen.add(cursor)
  } while (cursor)
  return memberships
}

const loadTaggedPatients = async (client: ReviewClient, signal: AbortSignal) => {
  const options = { fetchPolicy: 'network-only' as const, context: { fetchOptions: { signal } } }
  const tags = await client.query<GetAdvancedAllTagsQuery>({ query: GetAdvancedAllTagsDocument, ...options })
  const tag = tags.data.patientTags.find(({ name }) => name === ADVANCED_PROGRAM_TAG_NAME)
  const patients: ReviewPatient[] = []
  if (!tag) return { patients, hasAdvancedTag: false }
  let page = 0
  let patientCount: number
  do {
    abortIfNeeded(signal)
    // eslint-disable-next-line no-await-in-loop -- Read the next page only while the reported count requires it.
    const response = await client.query<
      SearchAdvancedMembershipReviewPatientsQuery,
      SearchAdvancedMembershipReviewPatientsQueryVariables
    >({
      query: SearchAdvancedMembershipReviewPatientsDocument,
      variables: { tagID: tag.id, page },
      ...options,
    })
    const result = response.data.searchPatientsV2
    patientCount = result.patientCount
    if (result.patients.length === 0 && page * PATIENT_PAGE_SIZE < patientCount) {
      throw new Error('Incomplete Standard caseload comparison')
    }
    patients.push(...result.patients)
    page += 1
  } while (page * PATIENT_PAGE_SIZE < patientCount)
  return { patients, hasAdvancedTag: true }
}

export const loadMembershipReview = async (client: ReviewClient, signal: AbortSignal): Promise<MembershipReview> => {
  const [memberships, tagged] = await Promise.all([loadMemberships(signal), loadTaggedPatients(client, signal)])
  const patients = new Map(tagged.patients.map((patient) => [patient.uid, patient]))
  const unlistedUids = [...new Set(memberships.map(({ uid }) => uid))].filter((uid) => !patients.has(uid))
  await mapWithConcurrency(chunk(unlistedUids, 200), 4, async (uids) => {
    abortIfNeeded(signal)
    const response = await client.query<
      GetAdvancedMembershipReviewPatientsQuery,
      GetAdvancedMembershipReviewPatientsQueryVariables
    >({
      query: GetAdvancedMembershipReviewPatientsDocument,
      variables: { uids },
      fetchPolicy: 'network-only',
      context: { fetchOptions: { signal } },
    })
    response.data.patients.forEach((patient) => patients.set(patient.uid, patient))
  })
  const unreadableUids = [...patients.values()].filter(({ careTeam }) => careTeam == null).map(({ uid }) => uid)
  return {
    memberships,
    patients: [...patients.values()],
    hasAdvancedTag: tagged.hasAdvancedTag,
    unavailablePatientUids: [...new Set([...unlistedUids.filter((uid) => !patients.has(uid)), ...unreadableUids])],
  }
}

export const reviewMembershipsByProvider = (
  memberships: AdvancedCareTeamMember[],
  patients: ReviewPatient[]
): ProviderMembershipReview[] => {
  const standardTeams = new Map(patients.map((patient) => [patient.uid, patient.careTeam]))
  const providers = new Map<string, { advanced: Map<string, Set<string>>; missing: Set<string> }>()
  const providerFor = (providerUid: string) => {
    let provider = providers.get(providerUid)
    if (!provider) {
      provider = { advanced: new Map(), missing: new Set() }
      providers.set(providerUid, provider)
    }
    return provider
  }
  memberships.forEach(({ uid, providerUid, role }) => {
    const provider = providerFor(providerUid)
    const roles = provider.advanced.get(uid) ?? new Set<string>()
    roles.add(role)
    provider.advanced.set(uid, roles)
  })
  patients.forEach((patient) => {
    patient.careTeam?.forEach(({ coachID }) => {
      const provider = providerFor(coachID)
      if (!provider.advanced.has(patient.uid)) provider.missing.add(patient.uid)
    })
  })
  return [...providers].map(([providerUid, provider]) => ({
    providerUid,
    advancedPatients: [...provider.advanced].map(([uid, roles]) => ({ uid, roles: [...roles].sort() })),
    missingMembershipPatients: [...provider.missing],
    missingStandardMembershipPatients: [...provider.advanced.keys()].filter((uid) => {
      const team = standardTeams.get(uid)
      return team != null && !team.some((member) => member.coachID === providerUid)
    }),
  }))
}
